import { Link } from "react-router-dom";
import {
  ArrowRight, MessageSquare, ClipboardList, FileSpreadsheet, Webhook,
  Smartphone, UserCheck, Send, CheckCircle2, Link2, ShieldCheck,
} from "lucide-react";
import SEO from "@/components/SEO";

const SOURCES = [
  "Messenger", "WhatsApp", "SMS", "Facebook", "Viber",
  "Phone calls", "Walk-ins", "Staff notes",
  "CSV files", "Google Sheets", "Website forms", "Zapier / Make / webhooks",
];

const STEPS = [
  {
    icon: ClipboardList,
    eyebrow: "Step 1",
    title: "Capture the details",
    desc: "Staff types a note, pastes a chat, imports a CSV or Google Sheet, or receives a website form / webhook submission. Only text staff intentionally enter or import is processed: ConfirmPH does not automatically read your private Messenger or WhatsApp.",
  },
  {
    icon: ShieldCheck,
    eyebrow: "Step 2",
    title: "ConfirmPH creates a draft",
    desc: "ConfirmPH helps extract possible booking details: customer name, dates, vehicle, pickup, service mode, price, and any advance / downpayment or refundable security deposit, and creates a Booking Draft. Missing fields stay missing, with clear warnings. If a note just says \"deposit\", the draft flags it for staff to clarify instead of guessing which one it is.",
  },
  {
    icon: UserCheck,
    eyebrow: "Step 3",
    title: "Staff reviews the draft",
    desc: "Staff checks customer name, dates, vehicle / item, service mode where provided, pickup and return, price, advance / downpayment, security deposit, and any notes, and fixes anything the extractor got wrong before approving.",
  },
  {
    icon: Send,
    eyebrow: "Step 4",
    title: "Send or copy confirmation link",
    desc: "Staff sends by email, by a configured channel (SMS / WhatsApp / Messenger where available), or manually copies the link back into the original chat. The manual copy fallback is always available.",
  },
  {
    icon: CheckCircle2,
    eyebrow: "Step 5",
    title: "Customer confirms",
    desc: "The customer reviews the specific booking and agreement version presented and confirms it or requests a change. Only then is the booking treated as customer-confirmed. If the business later makes a material change, the new version needs the renewed acceptance ConfirmPH's change rules require.",
  },
];

function StepCard({ icon: Icon, eyebrow, title, desc, n }) {
  return (
    <div
      data-testid={`intake-step-${n}`}
      className="relative rounded-2xl border border-slate-200 bg-white p-6 hover:border-emerald-300 transition-colors"
    >
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-700 grid place-items-center flex-shrink-0">
          <Icon className="w-5 h-5" strokeWidth={1.8} />
        </div>
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold">
            {eyebrow}
          </div>
          <h3 className="mt-1 font-display text-lg font-semibold text-slate-900">
            {title}
          </h3>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed">{desc}</p>
        </div>
      </div>
    </div>
  );
}

export default function BookingIntakeExplainer() {
  return (
    <div data-testid="businesses-intake-page">
      <SEO
        title="How Booking Intake Works: ConfirmPH for Rental Businesses"
        description="Turn notes, chats, spreadsheets, and website form submissions into booking drafts your staff can review and customers can confirm."
        canonical="/businesses/intake"
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-grid" data-testid="intake-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-12 sm:pb-16">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 text-xs font-medium">
              <ClipboardList className="w-3.5 h-3.5" /> Booking Intake Hub
            </div>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-display font-bold tracking-tight text-slate-900 leading-[1.05]">
              How <span className="text-[#1B4332]">Booking Intake</span> Works
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed">
              Turn notes, chats, spreadsheets, and website form submissions
              into booking drafts your staff can review and customers can
              confirm.
            </p>

            <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">
                Rental bookings often come from many places
              </div>
              <div className="mt-3 flex flex-wrap gap-2" data-testid="intake-sources">
                {SOURCES.map((s) => (
                  <span
                    key={s}
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-slate-200 bg-slate-50 text-xs text-slate-700"
                  >
                    <MessageSquare className="w-3 h-3 text-slate-400" />
                    {s}
                  </span>
                ))}
              </div>
              <p className="mt-4 text-sm text-slate-600 leading-relaxed">
                ConfirmPH helps turn those scattered details into clear
                booking drafts.{" "}
                <span className="font-medium text-slate-800">
                  Staff can type notes, paste chats, import files, or receive
                  connected form submissions
                </span>.
                ConfirmPH does not automatically read your private
                Messenger, WhatsApp, or SMS.
              </p>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/register?as=business"
                data-testid="intake-cta-trial-hero"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg btn-brand font-medium"
              >
                Start free trial <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/businesses"
                data-testid="intake-cta-back-hero"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 font-medium"
              >
                Back to ConfirmPH for Businesses
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Visual flow */}
      <section
        className="border-t border-slate-200 bg-[#F8FAF9]"
        data-testid="intake-flow"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-semibold">
              The flow
            </div>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold">
              From scattered details to customer-confirmed booking
            </h2>
            <p className="mt-3 text-slate-600 leading-relaxed">
              Two checkpoints, every time: staff review, then customer
              confirmation. Nothing is auto-confirmed.
            </p>
          </div>

          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {STEPS.map((s, i) => (
              <StepCard key={i} n={i + 1} {...s} />
            ))}
          </div>
        </div>
      </section>

      {/* Staff Notes shortcut */}
      <section
        className="border-t border-slate-200 bg-white"
        data-testid="intake-staff-notes"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            <div className="lg:col-span-7">
              <div className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-semibold">
                ConfirmPH Notepad
              </div>
              <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold">
                A simple notes shortcut for staff phones
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Staff can install a focused Notepad shortcut on their phone. They
                can save booking notes locally,{" "}
                <span className="font-medium text-slate-800">
                  even with bad internet
                </span>
                . Notes remain on that device until they are imported or
                deleted. Import requires internet, a business login, and
                Booking Intake permission.
              </p>

              <ul className="mt-6 space-y-3 text-sm">
                <li className="flex items-start gap-3">
                  <span className="w-7 h-7 rounded-md bg-emerald-100 text-emerald-700 grid place-items-center flex-shrink-0">
                    <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                  <span className="text-slate-700">
                    <span className="font-medium text-slate-900">
                      Saved locally
                    </span>:
                    the note autosaves on the device and remains there across
                    refreshes and app restarts.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-7 h-7 rounded-md bg-slate-100 text-slate-600 grid place-items-center flex-shrink-0">
                    <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                  <span className="text-slate-700">
                    <span className="font-medium text-slate-900">Create ConfirmPH Draft</span>:
                    authorized staff explicitly import the note while online.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-7 h-7 rounded-md bg-slate-100 text-slate-600 grid place-items-center flex-shrink-0">
                    <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                  <span className="text-slate-700">
                    <span className="font-medium text-slate-900">
                      Delete local note
                    </span>:
                    remove a note from the device when it is no longer needed.
                  </span>
                </li>
              </ul>
            </div>

            <div className="lg:col-span-5">
              <div
                className="rounded-3xl border border-slate-200 bg-[#F8FAF9] p-6 sm:p-8"
                data-testid="intake-staff-notes-card"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-700 grid place-items-center">
                    <Smartphone className="w-5 h-5" strokeWidth={1.8} />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-900">
                      ConfirmPH Notepad
                    </div>
                    <div className="text-xs text-slate-500">
                      Offline-first shortcut
                    </div>
                  </div>
                </div>
                <div className="mt-5 space-y-3">
                  <div className="rounded-xl border border-slate-200 bg-white p-3 text-sm">
                    <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">
                      Saved offline
                    </div>
                    <div className="mt-1 text-slate-800">
                      Maria, Toyota Vios, Aug 20 9am to Aug 21 9am, ₱1,800/day,
                      security deposit ₱2,000.
                    </div>
                  </div>
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
                    <div className="text-[10px] uppercase tracking-wider font-semibold">
                      Local only
                    </div>
                    <div className="mt-1">3 notes saved on this device.</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Imports & connected systems */}
      <section
        className="border-t border-slate-200 bg-[#F8FAF9]"
        data-testid="intake-imports"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-semibold">
              Works with your existing workflow
            </div>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold">
              Bring bookings in the way that fits your business
            </h2>
            <p className="mt-3 text-slate-600 leading-relaxed">
              Use what you already have: spreadsheets, your website, an
              automation tool, and let ConfirmPH consolidate the records.
              Official channels are clearly marked as “when configured”.
            </p>
          </div>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              {
                icon: FileSpreadsheet,
                title: "CSV import / export",
                desc: "Round-trip your existing booking spreadsheets in and out of ConfirmPH.",
              },
              {
                icon: FileSpreadsheet,
                title: "Google Sheets import",
                desc: "Import rows from a published or shared sheet. Connected Google access depends on platform configuration.",
              },
              {
                icon: Webhook,
                title: "Website forms via webhooks",
                desc: "Send your own form submissions into ConfirmPH as drafts with HMAC-signed payloads.",
              },
              {
                icon: Webhook,
                title: "Zapier / Make",
                desc: "Connect existing automations to ConfirmPH inbound webhooks.",
              },
              {
                icon: MessageSquare,
                title: "SMS / WhatsApp / Messenger sending",
                desc: "Send confirmations through configured official channels, when set up with your own credentials and approved templates.",
              },
              {
                icon: Link2,
                title: "Manual copy fallback",
                desc: "The “Copy confirmation message” button is always available: no integration required.",
              },
            ].map(({ icon: Icon, title, desc }, i) => (
              <div
                key={i}
                data-testid={`intake-channel-${i}`}
                className="rounded-xl border border-slate-200 bg-white p-5"
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 grid place-items-center flex-shrink-0">
                    <Icon className="w-4 h-4" strokeWidth={1.8} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-display text-sm font-semibold text-slate-900">
                      {title}
                    </h3>
                    <p className="mt-1 text-xs text-slate-600 leading-relaxed">
                      {desc}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <p className="mt-6 text-xs text-slate-500">
            Official SMS / WhatsApp / Messenger sending requires the
            business’s own provider credentials and (where applicable)
            approved templates from the provider. ConfirmPH does not scrape
            private chats and does not ask for personal social-platform
            passwords.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section
        className="border-t border-slate-200 bg-white"
        data-testid="intake-cta"
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold">
            Ready to try it with your next booking?
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Take your next booking the way you always have: Messenger, phone,
            walk-in, sheet, webhook. Drop the details into ConfirmPH and let
            staff review the draft before the customer confirms.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/register?as=business"
              data-testid="intake-cta-trial"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg btn-brand font-medium"
            >
              Start free trial <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/businesses/pricing"
              data-testid="intake-cta-pricing"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 font-medium"
            >
              See plans
            </Link>
            <Link
              to="/businesses"
              data-testid="intake-cta-back"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 font-medium"
            >
              Back to ConfirmPH for Businesses
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
